/**
 * GlobalAddToCart - A reusable class for handling add to cart functionality across the theme
 *
 * Features:
 * - Adds items to cart with proper loading states
 * - Handles error messages
 * - Updates cart count and cart drawer
 * - Integrates with Shopify's cart drawer and notification components
 * - Uses PUB_SUB_EVENTS for theme integration
 */
if (!window.GlobalAddToCart) {
    class GlobalAddToCart {
        constructor() {
            // Initialize PUB_SUB_EVENTS from theme or create fallbacks
            this.PUB_SUB_EVENTS = window.PUB_SUB_EVENTS || {
                cartUpdate: "cart:update",
                cartError: "cart:error",
            };

            // Initialize publish function or create fallback
            this.publish =
                window.publish ||
                function (eventName, data) {
                    const event = new CustomEvent(eventName, {
                        detail: data,
                        bubbles: true,
                    });
                    document.documentElement.dispatchEvent(event);
                };

            // Bind methods to ensure proper 'this' context
            this.addToCart = this.addToCart.bind(this);
            this.handleErrorMessage = this.handleErrorMessage.bind(this);
            this.updateCartCount = this.updateCartCount.bind(this);
            this.openCartDrawer = this.openCartDrawer.bind(this);

            // Initialize event listeners
            this.initEventListeners();
        }

        /**
         * Initialize event listeners for add to cart buttons
         * This uses event delegation for better performance and to handle dynamically added elements
         */
        initEventListeners() {
            document.addEventListener("click", (event) => {
                const addToCartButton = event.target.closest(
                    ".add-to-cart-button"
                );
                if (addToCartButton) {
                    event.preventDefault();
                    event.stopPropagation();

                    // Pass the button to the addToCart method
                    this.addToCart(addToCartButton);
                }
            });
        }

        /**
         * Main method to add an item to cart
         * @param {HTMLElement} button - The add to cart button element
         */
        addToCart(button) {
            // Return early if button is disabled or already loading
            if (
                button.classList.contains("loading") ||
                button.getAttribute("aria-disabled") === "true"
            ) {
                return;
            }

            const productId = button.getAttribute("data-product-id");
            const variantId = button.getAttribute("data-variant-id");
            const variantIds = button.getAttribute("data-variant-ids");
            const quantity = parseInt(
                button.getAttribute("data-quantity") || "1",
                10
            );

            // Show loading state
            button.classList.add("loading");
            button.setAttribute("aria-disabled", "true");

            // Get cart element (notification or drawer)
            const cart =
                document.querySelector("cart-notification") ||
                document.querySelector("cart-drawer");

            // Check if we have multiple variant IDs
            if (variantIds && variantIds.trim() !== "") {
                this.addMultipleToCart(
                    button,
                    variantIds.split(","),
                    quantity,
                    cart
                );
                return;
            }

            // Continue with single variant addition if no multiple variants
            if (!variantId) {
                console.error("No variant ID found to add to cart");
                this.handleErrorMessage(
                    button,
                    "Error: No variant ID found. Please select a product variant."
                );
                button.classList.remove("loading");
                button.removeAttribute("aria-disabled");
                return;
            }

            // Create FormData
            const formData = new FormData();
            formData.append("id", variantId);
            formData.append("quantity", quantity);

            // Check if this is a subscription purchase and add selling plan ID if present
            const sellingPlanId = button.getAttribute("data-selling-plan-id");
            if (sellingPlanId) {
                formData.append("selling_plan", sellingPlanId);
            }

            // Add sections for cart drawer update
            if (cart) {
                try {
                    // Try to get sections to render from cart's method
                    formData.append(
                        "sections",
                        cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append("sections_url", window.location.pathname);
                    cart.setActiveElement(document.activeElement);
                } catch (e) {
                    // Fallback if cart doesn't have the methods
                    formData.append(
                        "sections",
                        "cart-drawer,cart-drawer-items,cart-icon-bubble"
                    );
                    formData.append("sections_url", window.location.pathname);
                }
            }

            // Setup fetch config
            const config = {
                method: "POST",
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: formData,
            };

            // Add to cart using Fetch API
            fetch("/cart/add.js", config)
                .then((response) => response.json())
                .then((response) => {
                    if (response.status) {
                        // Handle error response
                        console.error(
                            "Error adding to cart:",
                            response.description
                        );

                        // Publish cart error event
                        this.publish(this.PUB_SUB_EVENTS.cartError, {
                            source: "global-add-to-cart",
                            productVariantId: variantId,
                            errors: response.errors || response.description,
                            message: response.message,
                        });

                        // Handle error message
                        this.handleErrorMessage(button, response.description);

                        button.classList.remove("loading");
                        button.removeAttribute("aria-disabled");
                        return;
                    }

                    // Remove loading state
                    button.classList.remove("loading");

                    // Show success state
                    button.classList.add("success");

                    // Clear any previous error messages
                    this.handleErrorMessage(button, false);

                    // Publish cart update event
                    this.publish(this.PUB_SUB_EVENTS.cartUpdate, {
                        source: "global-add-to-cart",
                        productVariantId: variantId,
                        cartData: response,
                    });

                    // Update cart count
                    this.updateCartCount();

                    // Handle cart drawer/notification
                    if (cart) {
                        try {
                            // Try to use cart's renderContents method
                            cart.renderContents(response);
                        } catch (e) {
                            console.error("Error rendering cart contents:", e);

                            // Fallback for cart drawer if renderContents fails
                            const cartDrawerItems =
                                document.querySelector("cart-drawer-items");
                            if (cartDrawerItems) {
                                try {
                                    cartDrawerItems.onCartUpdate();
                                } catch (err) {
                                    console.error(
                                        "Error updating cart drawer items:",
                                        err
                                    );
                                }
                            }
                        }
                    } else {
                        // If no cart notification/drawer, consider other options (page reload, custom notification)
                    }

                    // Open cart drawer
                    this.openCartDrawer();

                    // Reset button after delay
                    setTimeout(() => {
                        button.classList.remove("success");
                        button.removeAttribute("aria-disabled");
                    }, 2000);
                })
                .catch((error) => {
                    console.error("Error adding to cart:", error);
                    button.classList.remove("loading");
                    button.removeAttribute("aria-disabled");

                    // Handle error message
                    this.handleErrorMessage(
                        button,
                        "Sorry, there was an error adding this item to your cart. Please try again."
                    );
                });
        }

        /**
         * Add multiple items to cart sequentially
         * @param {HTMLElement} button - The add to cart button element
         * @param {Array<string>} variantIds - Array of variant IDs to add
         * @param {number} quantity - Quantity for each variant
         * @param {HTMLElement} cart - Cart drawer or notification element
         */
        addMultipleToCart(button, variantIds, quantity, cart) {
            // Filter out any empty variant IDs
            const validVariantIds = variantIds.filter(
                (id) => id && id.trim() !== ""
            );

            if (validVariantIds.length === 0) {
                console.error("No valid variant IDs found to add to cart");
                this.handleErrorMessage(
                    button,
                    "Error: No valid product variants found."
                );
                button.classList.remove("loading");
                button.removeAttribute("aria-disabled");
                return;
            }

            let currentIndex = 0;
            let lastResponse = null;

            const addNextItem = () => {
                // If we've added all items, finish up
                if (currentIndex >= validVariantIds.length) {
                    button.classList.remove("loading");
                    button.classList.add("success");

                    // Clear any previous error messages
                    this.handleErrorMessage(button, false);

                    // Update cart count
                    this.updateCartCount();

                    // Handle cart drawer/notification with last response
                    if (lastResponse && cart) {
                        try {
                            cart.renderContents(lastResponse);
                        } catch (e) {
                            console.error("Error rendering cart contents:", e);
                            const cartDrawerItems =
                                document.querySelector("cart-drawer-items");
                            if (cartDrawerItems) {
                                try {
                                    cartDrawerItems.onCartUpdate();
                                } catch (err) {
                                    console.error(
                                        "Error updating cart drawer items:",
                                        err
                                    );
                                }
                            }
                        }
                    }

                    // Open cart drawer
                    this.openCartDrawer();

                    // Reset button after delay
                    setTimeout(() => {
                        button.classList.remove("success");
                        button.removeAttribute("aria-disabled");
                    }, 2000);

                    return;
                }

                // Get the current variant ID
                const variantId = validVariantIds[currentIndex];

                // Create FormData
                const formData = new FormData();
                formData.append("id", variantId);
                formData.append("quantity", quantity);

                // Check if this is a subscription purchase and add selling plan ID if present
                const sellingPlanId = button.getAttribute(
                    "data-selling-plan-id"
                );
                if (sellingPlanId) {
                    formData.append("selling_plan", sellingPlanId);
                }

                // Add sections for cart drawer update
                if (cart) {
                    try {
                        formData.append(
                            "sections",
                            cart
                                .getSectionsToRender()
                                .map((section) => section.id)
                        );
                        formData.append(
                            "sections_url",
                            window.location.pathname
                        );
                        cart.setActiveElement(document.activeElement);
                    } catch (e) {
                        formData.append(
                            "sections",
                            "cart-drawer,cart-drawer-items,cart-icon-bubble"
                        );
                        formData.append(
                            "sections_url",
                            window.location.pathname
                        );
                    }
                }

                // Setup fetch config
                const config = {
                    method: "POST",
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                    },
                    body: formData,
                };

                // Add to cart using Fetch API
                fetch("/cart/add.js", config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            // Handle error response
                            console.error(
                                "Error adding to cart for routine:",
                                response.description
                            );
                            throw new Error(response.description);
                        }

                        // Save the response and move to the next item
                        lastResponse = response;
                        currentIndex++;
                        addNextItem();
                    })
                    .catch((error) => {
                        console.error("Error adding items to cart:", error);
                        button.classList.remove("loading");
                        button.removeAttribute("aria-disabled");

                        // Handle error message
                        this.handleErrorMessage(
                            button,
                            "Sorry, there was an error adding the items to your cart. Please try again."
                        );
                    });
            };

            // Start adding items
            addNextItem();
        }

        /**
         * Handle displaying error messages
         * @param {HTMLElement} button - The add to cart button
         * @param {string|boolean} errorMessage - The error message or false to clear errors
         */
        handleErrorMessage(button, errorMessage = false) {
            // Try to find an error message container near the button
            // First, look for a parent product container
            const productContainer =
                button.closest("[data-product-container]") ||
                button.closest(".product") ||
                button.closest(".cs-product-carousel__product") ||
                button.closest("[data-routine-container]");
            if (!productContainer) return;

            // Check if an error message wrapper already exists
            let errorMessageWrapper = productContainer.querySelector(
                ".product-form__error-message-wrapper"
            );

            // If no error message wrapper exists, create one
            if (!errorMessageWrapper && errorMessage) {
                errorMessageWrapper = document.createElement("div");
                errorMessageWrapper.className =
                    "product-form__error-message-wrapper";
                errorMessageWrapper.style.cssText =
                    "color: #e32c2b; text-align: left; padding: 10px 0; display: block;";

                const errorMessageElement = document.createElement("div");
                errorMessageElement.className = "product-form__error-message";
                errorMessageWrapper.appendChild(errorMessageElement);

                // Insert the error message wrapper before the button
                button.parentNode.insertBefore(errorMessageWrapper, button);
            }

            // If we have an error message wrapper, update or hide it
            if (errorMessageWrapper) {
                const errorMessageElement = errorMessageWrapper.querySelector(
                    ".product-form__error-message"
                );

                if (errorMessage && errorMessageElement) {
                    errorMessageElement.textContent = errorMessage;
                    errorMessageWrapper.style.display = "block";
                } else {
                    errorMessageWrapper.style.display = "none";
                }
            } else if (errorMessage) {
                // Fallback to alert if we couldn't find or create a wrapper
                alert(errorMessage);
            }
        }

        /**
         * Update the cart count indicator in the header
         */
        updateCartCount() {
            fetch("/cart.js")
                .then((response) => response.json())
                .then((cart) => {
                    const cartIconContainer = document.querySelector(
                        "#cart-icon-bubble .cart-icon-container"
                    );
                    let cartCountBubble = document.querySelector(
                        ".cart-count-bubble-new"
                    );

                    // Update cart icon
                    if (cart.item_count > 0) {
                        // Add cart-has-items class to container
                        if (cartIconContainer) {
                            cartIconContainer.classList.add("cart-has-items");
                        }

                        // Make filled cart icon visible
                        const filledCartIcon = document.querySelector(
                            "#cart-icon-bubble .cart-filled-icon"
                        );
                        if (filledCartIcon) {
                            filledCartIcon.style = "";
                        }

                        // Hide empty cart icon
                        const emptyCartIcon = document.querySelector(
                            "#cart-icon-bubble .cart-empty-icon"
                        );
                        if (emptyCartIcon) {
                            emptyCartIcon.style.display = "none";
                        }

                        let num_cart_items = 0;
                        cart.items.map((item) => {
                            if (item.handle != "package-protection") {
                                num_cart_items += item.quantity;
                            }
                        });

                        // Create the cart count bubble if it doesn't exist
                        if (!cartCountBubble) {
                            cartCountBubble = document.createElement("div");
                            cartCountBubble.classList.add(
                                "cart-count-bubble-new"
                            );

                            const countSpan = document.createElement("span");
                            countSpan.setAttribute("aria-hidden", "true");

                            const visibleSpan = document.createElement("span");
                            visibleSpan.classList.add("visually-hidden");
                            visibleSpan.textContent = `${num_cart_items} items`;

                            cartCountBubble.appendChild(countSpan);
                            cartCountBubble.appendChild(visibleSpan);

                            if (cartIconContainer) {
                                cartIconContainer.appendChild(cartCountBubble);
                            }
                        }

                        // Update count and make sure the bubble is visible
                        if (cartCountBubble) {
                            const countElement = cartCountBubble.querySelector(
                                'span[aria-hidden="true"]'
                            );
                            if (countElement) {
                                countElement.textContent = num_cart_items;
                            }
                            // Make count bubble visible
                            cartCountBubble.style.opacity = "1";
                        }

                        // Remove old cart-count-bubble if it exists
                        const oldCartCountBubble =
                            document.querySelector(".cart-count-bubble");
                        if (oldCartCountBubble) {
                            oldCartCountBubble.remove();
                        }
                    } else {
                        // If cart is empty, remove cart-has-items class
                        if (cartIconContainer) {
                            cartIconContainer.classList.remove(
                                "cart-has-items"
                            );
                        }

                        // Hide the count bubble if it exists
                        if (cartCountBubble) {
                            cartCountBubble.style.opacity = "0";
                        }
                    }
                })
                .catch((error) => console.error("Error fetching cart:", error));
        }

        /**
         * Open the cart drawer after adding an item
         */
        openCartDrawer() {
            // First fetch the latest cart data to ensure it's refreshed
            fetch("/cart.js")
                .then((response) => response.json())
                .then((cart) => {
                    // Small delay to ensure Shopify has processed the cart update
                    setTimeout(() => {
                        const cartDrawer =
                            document.querySelector("cart-drawer");
                        if (cartDrawer) {
                            cartDrawer.open();
                        }
                    }, 200); // 200ms delay
                })
                .catch((error) => {
                    console.error(
                        "Error fetching cart before opening drawer:",
                        error
                    );
                });
        }
    }

    // Make the class available globally
    window.GlobalAddToCart = GlobalAddToCart;
}

// Initialize the global add to cart functionality
document.addEventListener("DOMContentLoaded", function () {
    window.globalAddToCart =
        window.globalAddToCart || new window.GlobalAddToCart();
});
